import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  F0Dialog,
  F0Form,
  f0FormField,
  useF0Form,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import type { CustomFieldRenderProps } from '@factorialco/f0-react'
import type { AtsWorkplaceTypesEnum } from 'generated/resources/graphql'
import {
  AtsCreateAndAssignJobPostingFromVacancyDocument,
  AtsCreateAndAssignJobPostingModalDocument,
  AtsVacanciesTableDocument,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useSingleLegalEntitySelectorFormField } from 'modules/core/lib/formFields/legalEntitySelector'
import { useSingleLocationSelectorFormField } from 'modules/core/lib/formFields/locationSelector'
import { useSingleTeamSelectorFormField } from 'modules/core/lib/formFields/teamSelector'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { JobCatalogRoleSelector } from 'modules/job_catalog/components/JobCatalogRoleSelector'

import { NewJobPostingModalContentSkeleton } from 'modules/ats/pages/NewJobModalPage/Skeleton'

const WORKPLACE_TYPES = ['onsite', 'hybrid', 'remote'] as const

interface JobCatalogSelection {
  roleUuid: string | null
  levelUuid: string | null
}

interface FormValues {
  title: string
  jobCatalog: JobCatalogSelection
  locationId?: string
  workplaceType: AtsWorkplaceTypesEnum
  teamId?: string
  legalEntityId?: string
  allowReferral: boolean
  allowInternalApply: boolean
}

const CreateAndAssignJobPostingModal = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const location = useLocation()

  const state = location.state ?? {}
  const vacancyIds: string[] | undefined =
    'vacancyIds' in state && Array.isArray(state.vacancyIds) ? state.vacancyIds : undefined
  const vacanciesPath = navigation.ats.hiringPlan.vacancies.fullPath()

  const shouldPrefill = vacancyIds?.length === 1
  const vacancyId: string = (shouldPrefill && vacancyIds?.[0]) || '0'

  const { data: modalData, error } = useQuery(AtsCreateAndAssignJobPostingModalDocument, {
    variables: {
      vacancyId,
      shouldPrefill,
    },
    skip: !vacancyIds?.length,
  })

  const [createAndAssign, { client }] = useMutation(
    AtsCreateAndAssignJobPostingFromVacancyDocument,
    {
      extractorFn: (data) => data.atsMutations.createAndAssignJobPostingAtsVacancy,
    }
  )

  const vacancy = shouldPrefill ? modalData?.ats.vacancy : undefined

  const { formRef, submit } = useF0Form()

  const locationField = useSingleLocationSelectorFormField({
    mandatory: true,
    label: i18n.t('ats.jobs.new_job_modal.workplace_input_label'),
    placeholder: i18n.t('ats.jobs.new_job_modal.workplace_input_placeholder'),
    section: '',
    row: 'location-workplace',
  })

  const teamField = useSingleTeamSelectorFormField({
    mandatory: false,
    label: i18n.t('ats.jobs.new_job_modal.team_input_label'),
    placeholder: i18n.t('ats.jobs.new_job_modal.team_input_placeholder'),
    section: '',
    row: 'team-legal',
  })

  const legalEntityField = useSingleLegalEntitySelectorFormField({
    mandatory: true,
    label: i18n.t('ats.jobs.new_job_modal.legal_entity_input_label'),
    placeholder: i18n.t('ats.jobs.new_job_modal.legal_entity__input_placeholder'),
    section: '',
    row: 'team-legal',
  })

  const schema = useMemo(() => {
    const jobCatalogRolesExist = (modalData?.jobCatalog.treeNodesConnection.nodes ?? []).length > 0

    return z.object({
      title: f0FormField(z.string().min(1, i18n.t('common.validations.empty')), {
        label: i18n.t('ats.jobs.new_job_modal.title_input_label'),
        placeholder: i18n.t('ats.jobs.new_job_modal.title_input_placeholder'),
      }),
      jobCatalog: f0FormField(
        z.object({
          roleUuid: z.string().nullable(),
          levelUuid: z.string().nullable(),
        }),
        {
          label: '',
          fieldType: 'custom',
          renderIf: () => jobCatalogRolesExist,
          render: ({ value, onChange }: CustomFieldRenderProps<FormValues['jobCatalog']>) => (
            <JobCatalogRoleSelector
              onRoleChange={(roleUuid) => {
                onChange({
                  roleUuid: roleUuid ?? null,
                  levelUuid: null,
                })
              }}
              onLevelChange={(levelUuid) => {
                onChange({
                  ...value,
                  levelUuid: levelUuid ?? null,
                })
              }}
              selectedRoleUuid={value?.roleUuid ?? undefined}
              selectedRoleLevelUuid={value?.levelUuid ?? undefined}
            />
          ),
        }
      ),
      locationId: locationField,
      workplaceType: f0FormField(
        z.enum(WORKPLACE_TYPES, {
          required_error: i18n.t('common.validations.empty'),
        }),
        {
          label: i18n.t('ats.jobs.new_job_modal.workplace_type_input_label'),
          placeholder: i18n.t('ats.jobs.new_job_modal.workplace_type_input_placeholder'),
          fieldType: 'select',
          row: 'location-workplace',
          options: [
            {
              label: i18n.t('ats.workplace_types.onsite'),
              value: 'onsite' as const,
            },
            {
              label: i18n.t('ats.workplace_types.hybrid'),
              value: 'hybrid' as const,
            },
            {
              label: i18n.t('ats.workplace_types.remote'),
              value: 'remote' as const,
            },
          ],
        }
      ),
      teamId: teamField,
      legalEntityId: legalEntityField,
      allowReferral: f0FormField(z.boolean(), {
        label: i18n.t('ats.job_details.promotion.open_positions.allow_referral_label'),
        fieldType: 'checkbox',
        row: 'checkboxes',
      }),
      allowInternalApply: f0FormField(z.boolean(), {
        label: i18n.t('ats.job_details.promotion.open_positions.allow_internal_apply_label'),
        fieldType: 'checkbox',
        row: 'checkboxes',
      }),
    })
  }, [modalData, locationField, teamField, legalEntityField])

  const defaultValues = useMemo(
    () => ({
      title: vacancy?.name ?? '',
      jobCatalog: {
        roleUuid: vacancy?.jobCatalogTreeRoleUuid ?? null,
        levelUuid: vacancy?.jobCatalogTreeLevelUuid ?? null,
      },
      locationId: vacancy?.locationId ?? '',
      workplaceType: vacancy?.workplaceType,
      teamId: vacancy?.teamId ?? undefined,
      legalEntityId: '',
      allowReferral: false,
      allowInternalApply: false,
    }),
    [vacancy]
  )

  const handleSubmit = async ({ data }: { data: FormValues }) => {
    if (!vacancyIds?.length) return { success: false as const }

    const result = await createAndAssign({
      variables: {
        ids: vacancyIds,
        title: data.title,
        locationId: data.locationId,
        teamId: data.teamId || undefined,
        workplaceType: data.workplaceType,
        legalEntityId: data.legalEntityId,
        jobCatalogNodeRolesUuid: data.jobCatalog.roleUuid,
        jobCatalogNodeLevelsUuid: data.jobCatalog.levelUuid,
        allowReferral: data.allowReferral,
        allowInternalApply: data.allowInternalApply,
      },
    })

    let formErrors: Record<string, string> = {}
    const handled = handleResult(result, (errors) => {
      formErrors = errors
    })

    if (handled.success) {
      void client.refetchQueries({ include: [AtsVacanciesTableDocument] })

      const jobPostingId = handled.data?.vacancies?.[0]?.atsJobPostingId
      if (jobPostingId) {
        navigate(navigation.ats.jobs.jobsId.settings.fullPath({ jobId: jobPostingId }))
      } else {
        navigate(vacanciesPath)
      }

      return {
        success: true as const,
        message: i18n.t('ats.vacancies.create_and_assign_job_modal.success_message'),
      }
    }

    return { success: false as const, errors: formErrors }
  }

  const formDefinition = useF0FormDefinition({
    name: 'create-and-assign-job-posting',
    schema,
    defaultValues,
    submitConfig: { type: 'default', hideSubmitButton: true },
    errorTriggerMode: 'on-submit',
    onSubmit: handleSubmit,
  })

  const onClose = () => {
    navigate(vacanciesPath)
  }

  useEffect(() => {
    if (!vacancyIds?.length) {
      navigate(vacanciesPath, { replace: true })
    }
  }, [vacancyIds, vacanciesPath, navigate])

  if (!vacancyIds?.length) {
    return null
  }

  if (error) {
    return (
      <F0Dialog
        onClose={onClose}
        isOpen
        title={i18n.t('ats.vacancies.create_and_assign_job_modal.title')}
        secondaryAction={{
          label: i18n.t('common.close'),
          onClick: onClose,
        }}
      />
    )
  }

  const loading = !modalData

  return (
    <F0Dialog
      onClose={onClose}
      isOpen
      title={i18n.t('ats.vacancies.create_and_assign_job_modal.title')}
      primaryAction={{
        label: i18n.t('ats.vacancies.create_and_assign_job_modal.create_button'),
        onClick: submit,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      {loading ? (
        <NewJobPostingModalContentSkeleton />
      ) : (
        <F0Form formRef={formRef} formDefinition={formDefinition} />
      )}
    </F0Dialog>
  )
}

export default CreateAndAssignJobPostingModal
